import { ReactSortable } from 'react-sortablejs';
import type { IColumnInfo, IColumnRawInfo } from '@dbl/core/dataSource';
import type { ISortableOption } from '@/pages/datalab/components/SortableDrag/typeDefine';
import {checkNullString} from '@/pages/datalab/utils/utils';
import React, { useState, useMemo } from 'react';
import { initDragStatus } from '@/pages/datalab/components/SortableDrag/utils';
import { createRawColumnSortablePersist } from '@/pages/datalab/components/SortableDrag/utils/rawColumnSortablePersist';
import { TDispatchFun, SortableItems } from '@/pages/datalab/components/SortableDrag/configration';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { SortableAffix } from '@/pages/datalab/components/SortableDrag/Sortable/sortableAffix';
import {DropdownIcon} from "@/pages/datalab/components/SortableDrag/Sortable/dropdownIcon";
import {Dropdown, List} from "tea-component";
import {
  duplicateNameVerification,
  EditList,
  RenameWrapper
} from "@/pages/datalab/components/SortableDrag/FieldEditComponents";
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import { CustomColumn } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';
import { FieldType } from '@dbl/core/dataSource';
import { ChartTypeInfos } from '@dbl/core/chartEditor';
import {checkConditions} from "@/pages/datalab/pages/Multidimensional/utils";
import {ITableJoinRelation} from "@/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/interface";

export interface RawColumnSortableProps {
  sortableKey: string;
  sortableList: IColumnInfo[];
  sortableOption: ISortableOption;
  dispatchFun: TDispatchFun;
  isEn: boolean;
  dataTables: IGetTableInfoDto[];
  chartType: string;
  showCustomColumnModel: (column: IColumnInfo) => void;
  dataTableJoinRelations: ITableJoinRelation[];
  system: string;
  saveColumnInfos: any;
  customColumns?: CustomColumn[];
}
export const RawColumnSortable = (props: RawColumnSortableProps) => {
  const { sortableKey, sortableList, sortableOption, dispatchFun, isEn, dataTables, chartType, showCustomColumnModel, dataTableJoinRelations, system, saveColumnInfos, customColumns } = props;
  const { dragState, dragMoveHandle } = sortableOption;
  const [currentDragItem, setCurrentDragItem] = useState<IColumnInfo>();

  /** 原始列区写回逻辑单例：close / 拖拽落库 / 批量下拉增删共用同一套副作用 */
  const rawColumnPersist = useMemo(
    () =>
      createRawColumnSortablePersist({
        sortableKey,
        chartType,
        dispatchFun,
        saveColumnInfos,
      }),
    [sortableKey, chartType, dispatchFun, saveColumnInfos],
  );

  const {
    errorDrag: { [sortableKey]: errorDragType },
    currentDrag,
  } = dragState;
  
  
  const updateHandle = (column: IColumnInfo, updateColumn: any) => {

    dispatchFun(
      'updateTableField',
      {
        table_id: column.table_id,
        isEn,
        lan: isEn ? 'en' : 'zh',
        column,
        updateColumn,
      },
    );
  };
  
  const {dragSuffixText, disabledDrag, disabledError } = useMemo(() => {
    const {dimension} = ChartTypeInfos[chartType] || {};
    const dimensionList = sortableList.slice(0, -2);
    const {isCheckSuccess, info, disabledError} = checkConditions(dimension, dimensionList, isEn);
    const disabledDrag = (dimension.operator === '===') && sortableList.length - 2 === dimension.count;
    // 对字段类型的限制
    
    return {
      dragSuffixText: isCheckSuccess ? '' : info,
      disabledDrag,
      disabledError: disabledDrag && currentDrag ? disabledError : '',
    };
  }, [chartType, isEn, sortableList, currentDrag]);

  const batchAddConfig = {
    enabled: true,
    targetKey: 'columnRawColumns' as const,
    dataTables,
    customColumns: customColumns || [],
    existingColumns: sortableList.filter(item => !checkNullString(item.id)),
    dispatchFun,
    chartType,
    isEn,
    saveColumnInfos,
    rawColumnPersist,
  };

  return (
    <ReactSortable
      group="groupName"
      animation={100}
      filter={'.data-label-disabled'}
      handle={'.data-label-tips'}
      ghostClass="data-label-tips-green"
      delay={50}
      list={sortableList}
      forceFallback={true}
      className={`form-data-label-tips dimensions-content ${sortableKey}-${SortableItems} ${
        dragState.currentDrag === sortableKey ? 'dimensions-acitve' : ''
      }`}
      setList={(list) => {
        if (dragState.currentDrag === sortableKey && !checkNullString(errorDragType) || disabledDrag) {
          return false;
        }
        if (JSON.stringify(sortableList) !== JSON.stringify(list)) {
          rawColumnPersist.saveList(list);
        }
        return undefined;
      }}
      onMove={(evt: any, originalEvent: any) => {
        if (currentDragItem) {
          return dragMoveHandle(evt, originalEvent, dispatchFun, currentDragItem);
        }
        return false;
      }}
      onEnd={() => {
        initDragStatus(dispatchFun);
      }}
      onStart={(evt) => {
        setCurrentDragItem(sortableList[evt.oldIndex || 0]);
      }}
    >
      {sortableList.map((item, index) =>
        item.id ? (<RenameWrapper
          key={index}
          className={'green'}
          isEn={isEn}
          oldName={isEn ? item.column_display_en : item.column_display}
          changeRename={(name: string) => {
            const columns = dataTables?.find(({table_id}) => table_id === item.table_id)?.column_list || [];
            if (duplicateNameVerification(columns, name, isEn)) {
              return isEn ? 'Duplicate naming' : '重复命名';
            }
            updateHandle(item, {display_name: name});
          }}
          renderItem={(ref: any, clickRename: Function) => (
            <div className={`data-label-tips green`} key={item.id + item.table_code}>
              <Dropdown
                matchButtonWidth
                boxClassName={'dbl-multi-metric-drop-box'}
                // disabled={loadingIds.has(item.id)}
                updateOnChildrenChange={true}
                placementOffset={[-7, 5]}
                clickClose={false}
                button={<div className="dt-multiple-dropdown-group"><div className="tea-dropdown__value dt-multiple-dropdown-text" style={{ paddingRight: '6px' }}>{`${item.column_display || item.column_name}`} </div> <DropdownIcon /></div>}
              >
                {
                  (close) => (<EditList
                    options={item?.column_custom ?
                      [
                        {type: 'custom', render: () => (<List.Item onClick={(e) => {
                          e.stopPropagation();
                          showCustomColumnModel(item);
                          close();
                        }
                        }>{isEn ? 'Edit' : '编辑'}</List.Item>)},
                        {type: 'fieldType', system, value: item.column_type, decimal: item.accuracy, chartType, onChange: (column: IColumnRawInfo, updateInfo: any) => {
                          dispatchFun('updateCustomColumn',{
                            updateColumn: {...column, ...updateInfo}
                          });
        
                        },
                          
                        customDateCheckParams: { // 用于自定义字段 转换日期格式的传参
                          expression: item.sql_expression,
                          table_id: dataTables[0]?.table_id || '',
                          column: 'CustomField',
                          custom_column: {
                            custom_columns: [{
                              name_cn: 'CustomField',
                              column_type: 'float',
                              name_en: 'CustomField',
                              sql_expression: item.sql_expression,
                              accuracy: 2,
                            }],
                            data_tables: dataTables,
                            table_infos: dataTableJoinRelations,
                            metrics: [
                              {
                                table_code: 't0',
                                table_id: '',
                                column: 'CustomField',
                                label: 'CustomField',
                                label_en: 'CustomField',
                                column_type: 'string',
                                aggregate: '',
                                accuracy: 2,
                                to_date_format: '',
                              }
                            ]
                          }
                        }
      
                        },
                        {type: 'displayFormat', disabled: ![FieldType.Number, FieldType.Percent, FieldType.Float].includes(item?.column_type), hideUnit: item?.column_type === FieldType.Percent, accuracy: item.accuracy, unit: item.unit, onChange: (column: IColumnRawInfo, updateInfo: any) => {
                          dispatchFun('updateCustomColumn',{
                            updateColumn: {...updateInfo, id: item.id, column_type: item.column_type}
                          });
                        }},
                      ] :[
                        {type: 'rename', onClick: clickRename},
                        {type: 'fieldType', value: item.column_type, decimal: item.accuracy, date_format: item.date_format, chartType, onChange: updateHandle},
                        {type: 'displayFormat', disabled: ![FieldType.Number, FieldType.Percent, FieldType.Float].includes(item?.column_type), hideUnit: item?.column_type === FieldType.Percent, accuracy: item.accuracy, unit: item.unit, onChange: updateHandle},
                      ]}
                    close={close}
                    column={item}
                  />)
                }
  
              </Dropdown>
              <div
                className="close-icon"
                onClick={() => {
                  rawColumnPersist.removeItems(sortableList, [item]);
                }}
              >
                <i className="selectOther-flex-icon">
                  <SvgIcon type="clonse" />
                </i>
              </div>
            </div>
          )}/>)  : (
          <SortableAffix key={index} item={item} dragState={dragState} sortableKey={sortableKey} dragSuffixText={dragSuffixText} disabledDrag={disabledDrag} disabledError={disabledError} batchAddConfig={batchAddConfig}/>
        ),
      )}
    </ReactSortable>
  );
};
